import { TheoryEveningRequest } from "../../features/organization/contracts/TheoryEveningRequest";

/** The licence classes the curriculum templates ship, in the order the form shows them. */
export const LICENCE_CLASSES: string[] = ["B", "A", "A1", "A2", "AM", "BE", "L", "T"];

/** .NET DayOfWeek names, Monday first. */
export const WEEKDAYS: string[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const MAX_EVENINGS = 5;
export const DEFAULT_CAPACITY = 20;

export const defaultEvenings = (): TheoryEveningRequest[] => [
  { dayOfWeek: "Tuesday", timeLocal: "18:30" },
  { dayOfWeek: "Thursday", timeLocal: "18:30" },
];

/** The next Monday after today (local calendar), as "YYYY-MM-DD". */
export const nextMonday = (today: Date = new Date()): string => {
  const daysAhead = (8 - today.getDay()) % 7 || 7;
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysAhead);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The first theory date the preset creates: the earliest evening on or after the start
 * date ("YYYY-MM-DD"); the start date itself when there are no evenings.
 */
export const firstTheoryDate = (startDate: string, evenings: TheoryEveningRequest[]): string => {
  const [year, month, day] = startDate.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(start.getTime()) || evenings.length === 0) return startDate;
  const startIndex = (start.getUTCDay() + 6) % 7; // Monday = 0
  const offsets = evenings
    .map((evening) => WEEKDAYS.indexOf(evening.dayOfWeek))
    .filter((index) => index >= 0)
    .map((index) => (index - startIndex + 7) % 7);
  if (offsets.length === 0) return startDate;
  const first = new Date(start.getTime() + Math.min(...offsets) * 86_400_000);
  return first.toISOString().slice(0, 10);
};

/** The calendar opened on the week of a date. */
export const calendarWeekPath = (calendarRoute: string, date: string): string => `${calendarRoute}?date=${date}`;

/** The weekday's name in the display language (2024-01-01 was a Monday). */
export const weekdayLabel = (dayOfWeek: string, locale: string): string => {
  const index = WEEKDAYS.indexOf(dayOfWeek);
  return new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(
    new Date(Date.UTC(2024, 0, 1 + Math.max(0, index)))
  );
};

/** The first weekday no evening uses yet, for a new row. */
export const nextFreeWeekday = (evenings: TheoryEveningRequest[]): string =>
  WEEKDAYS.find((day) => !evenings.some((e) => e.dayOfWeek === day)) ?? WEEKDAYS[0];
